/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import {FocusableElement} from '@react-types/shared';
import {mergeProps} from '../utils/mergeProps';
import {PressProps} from './usePress';
import {PressResponderContext} from './context';
import React, {ForwardedRef, JSX, ReactNode, useContext, useEffect, useMemo, useRef} from 'react';
import {useObjectRef} from '../utils/useObjectRef';
import {useSyncRef} from '../utils/useSyncRef';

interface PressResponderProps extends PressProps {
  children: ReactNode;
}

export const PressResponder: React.ForwardRefExoticComponent<
  PressResponderProps & React.RefAttributes<FocusableElement>
> = React.forwardRef(
  ({children, ...props}: PressResponderProps, ref: ForwardedRef<FocusableElement>) => {
    let isRegistered = useRef(false);
    let prevContext = useContext(PressResponderContext);
    // oxlint-disable-next-line react/react-compiler
    let context: any = mergeProps(prevContext || {}, {
      ...props,
      register() {
        isRegistered.current = true;
        if (prevContext) {
          prevContext.register();
        }
      }
    });

    context.ref = useObjectRef(ref || prevContext?.ref);
    useSyncRef(prevContext, context.ref);

    useEffect(() => {
      if (!isRegistered.current) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            'A PressResponder was rendered without a pressable child. ' +
              'Either call the usePress hook, or wrap your DOM node with <Pressable> component.'
          );
        }
        isRegistered.current = true; // only warn once in strict mode.
      }
    }, []);

    return (
      <PressResponderContext.Provider value={context}>{children}</PressResponderContext.Provider>
    );
  }
);

export function ClearPressResponder({children}: {children: ReactNode}): JSX.Element {
  let context = useMemo(() => ({register: () => {}}), []);
  return (
    <PressResponderContext.Provider value={context}>{children}</PressResponderContext.Provider>
  );
}
